"use client";

import { useRef, useState, type ReactNode } from "react";
import { useLocale } from "@/i18n/LocaleProvider";
import { useResettableDateInput } from "@/lib/resettableDate";
import { openNativePicker } from "./Field";
import { cn } from "./cn";
import { Spinner } from "./Spinner";

/** Turns the raw draft into the value to save, or throws an Error whose
 *  message is shown under the field. `null` means "clear the value". */
export type InlineEditNormalize = (raw: string) => string | null;

const defaultNormalize: InlineEditNormalize = (raw) => raw.trim() || null;

/** Normalizer for non-negative decimals; accepts a comma as the separator. */
export const nonNegativeNumber: InlineEditNormalize = (raw) => {
  const next = raw.trim().replace(",", ".");
  if (!next) return null;
  if (!(Number(next) >= 0)) throw new Error("≥ 0");
  return next;
};

interface UseInlineEditOptions {
  value: string | null;
  onSave: (next: string | null) => Promise<void>;
  normalize?: InlineEditNormalize;
  /** Treat two values as equal (e.g. "95.20" vs "95.2") to skip a save. */
  equals?: (a: string | null, b: string | null) => boolean;
}

/**
 * State machine behind every tap-to-edit control: freezes the resting width
 * so the editor never resizes, saves on blur / Enter, cancels on Escape, and
 * skips the request when nothing changed.
 */
export function useInlineEdit(
  ref: React.RefObject<HTMLElement | null>,
  {
    value,
    onSave,
    normalize = defaultNormalize,
    equals = (a, b) => a === b,
  }: UseInlineEditOptions
) {
  const [editing, setEditing] = useState(false);
  const [width, setWidth] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function start() {
    setWidth(ref.current?.offsetWidth ?? null);
    setDraft(value ?? "");
    setError(null);
    setEditing(true);
  }

  async function commit(raw: string = draft) {
    if (saving) return;
    let next: string | null;
    try {
      next = normalize(raw);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    }
    if (equals(next, value ?? null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  const inputProps = {
    autoFocus: true,
    value: draft,
    disabled: saving,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value),
    onBlur: () => void commit(),
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void commit();
      } else if (e.key === "Escape") {
        setEditing(false);
      }
    },
  };

  return { editing, width, saving, error, start, commit, inputProps };
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn("size-4 shrink-0 md:size-3 text-muted opacity-60 group-hover:text-foreground group-hover:opacity-100", className)}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

interface InlineEditBaseProps {
  /** Raw value the editor starts from (ISO date for `type="date"`). */
  value: string | null;
  /** Saves the normalized value; throw to show the error and stay editing. */
  onSave: (next: string | null) => Promise<void>;
  /** What is shown at rest; defaults to `value`. */
  display?: ReactNode;
  normalize?: InlineEditNormalize;
  equals?: (a: string | null, b: string | null) => boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  disabled?: boolean;
  className?: string;
}

export interface EditableChipProps extends InlineEditBaseProps {
  label: string;
}

/** Pill with the same size as a `Badge size="md"` that edits its value in place. */
export function EditableChip({
  label,
  display,
  inputMode,
  disabled,
  className,
  ...options
}: EditableChipProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const edit = useInlineEdit(ref, options);
  const chip = "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm";
  const shown = display === undefined ? options.value : display;

  if (!edit.editing) {
    return (
      <button
        ref={ref}
        type="button"
        onClick={edit.start}
        disabled={disabled}
        title={label}
        className={cn(
          chip,
          "group border-border bg-surface-muted text-foreground transition-colors hover:border-muted/50 disabled:pointer-events-none",
          className
        )}
      >
        <span className="font-normal">{label}</span>
        <span className="font-medium tabular-nums">{shown ?? "—"}</span>
        {!disabled && <PencilIcon className="ml-0.5" />}
      </button>
    );
  }

  return (
    <span className="inline-flex flex-col gap-0.5">
      <span
        style={edit.width ? { width: edit.width } : undefined}
        className={cn(chip, "border-muted/50 bg-surface-muted text-foreground", className)}
      >
        <span className="font-normal">{label}</span>
        <input
          {...edit.inputProps}
          type="text"
          inputMode={inputMode}
          aria-label={label}
          className="h-5 w-0 min-w-0 flex-1 bg-transparent p-0 text-sm leading-5 font-medium tabular-nums text-foreground outline-none"
        />
        {edit.saving && <Spinner size="sm" />}
      </span>
      {edit.error && <span className="px-3 text-xs text-danger">{edit.error}</span>}
    </span>
  );
}

export interface InlineTextProps extends InlineEditBaseProps {
  /** Accessible name of the field. */
  label: string;
  type?: "text" | "date";
  placeholder?: string;
}

/**
 * Plain text that becomes an input on tap — for table cells and `<dd>`s.
 * Looks like the surrounding text at rest (plus a faint pencil); while
 * editing it keeps its width and only gains a muted background.
 */
export function InlineText({
  label,
  display,
  type = "text",
  placeholder,
  inputMode,
  disabled,
  className,
  ...options
}: InlineTextProps) {
  if (type === "date") {
    return (
      <InlineDate label={label} display={display} disabled={disabled} className={className} {...options} />
    );
  }
  return (
    <InlineTextField
      label={label}
      display={display}
      placeholder={placeholder}
      inputMode={inputMode}
      disabled={disabled}
      className={className}
      {...options}
    />
  );
}

function InlineTextField({
  label,
  display,
  placeholder,
  inputMode,
  disabled,
  className,
  ...options
}: Omit<InlineTextProps, "type">) {
  const ref = useRef<HTMLButtonElement>(null);
  const edit = useInlineEdit(ref, options);
  const shown = display === undefined ? options.value : display;
  // Phones: 44px tap target that grows the row; md+: compact.
  const box = "-mx-2 inline-flex min-h-11 max-w-full items-center gap-1.5 rounded px-2 md:-my-1 md:min-h-0 md:gap-1 md:py-1";

  if (disabled) return <span className={className}>{shown ?? "—"}</span>;

  if (!edit.editing) {
    return (
      <button
        ref={ref}
        type="button"
        onClick={edit.start}
        title={label}
        className={cn(
          box,
          "group text-left transition-colors hover:bg-surface-muted",
          className
        )}
      >
        <span className="min-w-0">{shown ?? "—"}</span>
        <PencilIcon />
      </button>
    );
  }

  return (
    <span className="inline-flex max-w-full flex-col gap-0.5">
      <span
        style={edit.width ? { width: edit.width } : undefined}
        // The frozen resting width can be tiny ("—"); keep room to type.
        className={cn(box, "min-w-18 bg-surface-muted", className)}
      >
        <input
          {...edit.inputProps}
          type="text"
          inputMode={inputMode}
          placeholder={placeholder}
          aria-label={label}
          className="w-0 min-w-0 flex-1 bg-transparent p-0 text-inherit leading-[inherit] outline-none"
        />
        {edit.saving && <Spinner size="sm" />}
      </span>
      {edit.error && <span className="text-xs text-danger">{edit.error}</span>}
    </span>
  );
}

/** iOS / iPadOS Safari (iPadOS reports itself as a Mac with touch). */
function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/**
 * Date variant of InlineText. The resting text never changes: a transparent
 * native `<input type="date">` covers it, so a tap opens the system picker
 * straight away (the only way that reliably works on iOS, where
 * `showPicker()` / programmatic focus can't open it) and the width stays put.
 *
 * Saving: desktop/Android pickers fire one `change` per pick, so that saves.
 * iOS fires `change` on every wheel turn, so there the pick is held as a
 * draft and saved when the picker closes (blur). iOS "Reset" clears the date —
 * see useResettableDateInput.
 */
function InlineDate({
  label,
  display,
  disabled,
  className,
  ...options
}: Omit<InlineTextProps, "type" | "placeholder" | "inputMode">) {
  const ref = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const edit = useInlineEdit(ref, options);
  const current = options.value?.slice(0, 10) ?? "";
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? current;
  useResettableDateInput(inputRef, "date");
  const shown = display === undefined ? options.value : display;

  if (disabled) return <span className={className}>{shown ?? "—"}</span>;

  return (
    <span className="inline-flex max-w-full flex-col gap-0.5">
      <span
        ref={ref}
        className={cn(
          "group relative -mx-2 inline-flex min-h-11 items-center gap-1.5 rounded px-2 md:-my-1 md:min-h-0 md:gap-1 md:py-1 transition-colors hover:bg-surface-muted",
          className
        )}
      >
        <span className="shrink-0 whitespace-nowrap">{shown ?? "—"}</span>
        {edit.saving ? <Spinner size="sm" /> : <PencilIcon />}
        <input
          ref={inputRef}
          type="date"
          aria-label={label}
          title={label}
          value={value}
          disabled={edit.saving}
          onClick={(e) => openNativePicker(e.currentTarget)}
          onChange={(e) => {
            const next = e.target.value;
            if (isIos()) setDraft(next);
            else void edit.commit(next);
          }}
          onBlur={() => {
            if (draft === null) return;
            const next = draft;
            setDraft(null);
            void edit.commit(next);
          }}
          // `appearance-none` lets iOS size it (auto-zoom: see app/layout.tsx).
          className="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
        />
      </span>
      {edit.error && <span className="text-xs text-danger">{edit.error}</span>}
    </span>
  );
}

export interface InlineBoolProps {
  /** Accessible name of the field. */
  label: string;
  value: boolean;
  /** Saves the picked value; throw to show the error and stay editing. */
  onSave: (next: boolean) => Promise<void>;
  /** What is shown at rest; defaults to the yes / no label. */
  display?: ReactNode;
  yesLabel?: string;
  noLabel?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Yes / no counterpart of InlineText. At rest it reads like the surrounding
 * text (plus a faint pencil); a tap swaps in a two-option segmented control —
 * picking an option saves it, Escape or tapping elsewhere cancels. Two
 * explicit options rather than a toggle, so a stray tap never flips a value.
 */
export function InlineBool({
  label,
  value,
  onSave,
  display,
  yesLabel,
  noLabel,
  disabled,
  className,
}: InlineBoolProps) {
  const { dict } = useLocale();
  const yes = yesLabel ?? dict.confirm.yes;
  const no = noLabel ?? dict.confirm.no;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shown = display === undefined ? (value ? yes : no) : display;
  // Phones: 44px tap target that grows the row; md+: compact.
  const box = "-mx-2 inline-flex min-h-11 max-w-full items-center gap-1.5 rounded px-2 md:-my-1 md:min-h-0 md:gap-1 md:py-1";

  if (disabled) return <span className={className}>{shown}</span>;

  async function pick(next: boolean) {
    if (saving) return;
    if (next === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setError(null);
          setEditing(true);
        }}
        title={label}
        className={cn(box, "group text-left transition-colors hover:bg-surface-muted", className)}
      >
        <span className="min-w-0">{shown}</span>
        <PencilIcon />
      </button>
    );
  }

  const option = (optionValue: boolean, optionLabel: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={value === optionValue}
      autoFocus={value === optionValue}
      disabled={saving}
      onClick={() => void pick(optionValue)}
      className={cn(
        // 44px tall tap target on phones; compact from `md` up (tables).
        "min-h-11 min-w-12 rounded px-3 text-sm font-medium transition-colors md:min-h-7",
        value === optionValue
          ? "bg-accent text-white"
          : "text-muted hover:bg-surface hover:text-foreground"
      )}
    >
      {optionLabel}
    </button>
  );

  return (
    <span className="inline-flex max-w-full flex-col gap-0.5">
      <span
        role="radiogroup"
        aria-label={label}
        onBlur={(e) => {
          if (!saving && !e.currentTarget.contains(e.relatedTarget)) setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setEditing(false);
        }}
        className={cn("inline-flex items-center gap-1 rounded-lg bg-surface-muted p-1", className)}
      >
        {option(true, yes)}
        {option(false, no)}
        {saving && <Spinner size="sm" />}
      </span>
      {error && <span className="text-xs text-danger">{error}</span>}
    </span>
  );
}
